import { Anchor } from "@mantine/core";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AuthFlowScaffold } from "../auth/components/AuthFlowScaffold";
import { AuthLegalConsent } from "../auth/components/AuthLegalConsent";
import { useAuthFlow } from "../auth/hooks/useAuthFlow";
import { safeReturnTo } from "../auth/rules/returnTo";
import type { FlowSubmitBody } from "../auth/rules/utils";
import { setOnboardingIntent } from "../onboarding/rules/intent";

/**
 * registrationSearchSchema validates the /registration search params: flow is the
 * resumed flow id, login_challenge/return_to carry an OIDC round-trip and the
 * post-signup landing target.
 */
const registrationSearchSchema = z.object({
  flow: z.string().optional(),
  login_challenge: z.string().optional(),
  return_to: z.string().optional(),
});

/**
 * Route is /registration. It initializes (or resumes) the Ory registration flow
 * (email + password + first/last name + a newsletter opt-in) and renders it
 * generically: whether Ory serves those fields on one screen or splits them into
 * a traits screen followed by a credential screen is its configuration to make,
 * and the route special-cases neither. On success the new registrant
 * already holds a session, so they land in the app rather than on a verification
 * screen: email verification is non-blocking (Ory emails the link, which resolves
 * out-of-band on the /verification route). Every new registrant is routed into
 * the create-organization step on their first authenticated landing, so the
 * submit records that intent before navigating. Cross-link to login.
 */
export const Route = createFileRoute("/_auth/registration")({
  component: RegistrationRoute,
  validateSearch: registrationSearchSchema,
});

function RegistrationRoute() {
  const { t } = useTranslation("iam");
  const search = Route.useSearch();
  const state = useAuthFlow("registration", {
    defaultRedirect: safeReturnTo(search.return_to),
    flowId: search.flow,
    initOptions: {
      loginChallenge: search.login_challenge,
    },
  });

  // Acceptance of the Terms and the Privacy Policy, which is mandatory to sign up. It is
  // held here rather than inside the consent control because Ory serves registration as a
  // two-step flow (traits, then the credential) and the form remounts its controls on each
  // step: state inside the control would be cleared as the flow advances, re-blocking a
  // registrant who already accepted on the screen before.
  const [accepted, setAccepted] = useState(false);

  // Every new registrant is routed into the create-organization step on their first
  // authenticated landing, so the submit always records that intent. The redirect itself
  // is owned by the _app guard, which consumes the intent. Recording before the navigating
  // submit is safe because the intent lives in sessionStorage, surviving the full-page
  // navigation.
  //
  // The acceptance is re-checked here rather than trusted from the disabled submit alone:
  // the disabled button is the affordance, this is the rule. A form submitted by any other
  // route (the Enter key on a field, a password manager, an autofill extension) reaches
  // this handler with the button never clicked.
  const handleSubmit = useCallback(
    (body: FlowSubmitBody) => {
      if (!accepted) {
        return;
      }

      setOnboardingIntent();

      return state.submit(body);
    },
    [accepted, state.submit],
  );

  return (
    <AuthFlowScaffold
      gate={<AuthLegalConsent accepted={accepted} onChange={setAccepted} />}
      gateSatisfied={accepted}
      onSubmit={handleSubmit}
      state={state}
      subtitle={t("registration.subtitle")}
      title={t("registration.title")}
    >
      <Anchor component={Link} size="sm" to="/login">
        {t("registration.alreadyHaveAccount")}
      </Anchor>
    </AuthFlowScaffold>
  );
}
